/* Capture service API — consent-gated call recording (§11).

   Same-origin via Caddy (/atrium/api/capture*), Bearer-authenticated with the
   Matrix access token, same shape as atriumApi. The live session state itself
   arrives over /sync (app.atrium.capture.* room state); this API is how the
   client acts on it — and how it fetches the authoritative per-user view
   (my_consent, the pending roster). */
import { matrix } from '@/lib/matrix'

export type CapturePolicy = 'host' | 'consensus' | 'forbidden'

export interface CaptureSession {
  id: string
  room_id: string
  origin: 'manual' | 'calendar'
  state: 'pending_consent' | 'recording' | 'stopping' | 'transcribing' | 'transcribed' | 'done' | 'cancelled' | 'failed'
  by: string
  policy: CapturePolicy
  detail?: string
  partial?: boolean
  since: number
  expires_at?: number
  started_at?: number
  stopped_at?: number
  pending?: string[]
  accepted?: string[]
  my_consent: 'accepted' | 'declined' | null
}

export interface CaptureRoomInfo {
  enabled: boolean
  policy: CapturePolicy
  source: 'state' | 'default'
  host: string | null
  session: CaptureSession | null
}

export interface TranscriptSegment {
  mxid: string
  start_ms: number
  end_ms: number
  text: string
}

export interface CaptureTranscript {
  session_id: string
  language: string
  partial: boolean
  segments: TranscriptSegment[]
  text: string
}

export class CaptureApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = matrix.getSession()?.accessToken
  if (token) headers['Authorization'] = 'Bearer ' + token
  let res: Response
  try {
    res = await fetch(path, { method, headers, body: body !== undefined ? JSON.stringify(body) : undefined })
  } catch {
    throw new CaptureApiError('Could not reach the capture service.', 0)
  }
  let data: Record<string, any> = {}
  try {
    data = await res.json()
  } catch {
    /* empty body */
  }
  if (!res.ok) throw new CaptureApiError(data.error || `${method} ${path} failed (${res.status})`, res.status)
  return data as T
}

export const captureApi = {
  /** The room's recording picture in one call: feature flag, policy, host,
      live session (with my consent). */
  info: (roomId: string) =>
    api<CaptureRoomInfo>('GET', `/atrium/api/capture/session?room_id=${encodeURIComponent(roomId)}`),
  start: (roomId: string) => api<{ session: CaptureSession }>('POST', '/atrium/api/capture/start', { room_id: roomId }),
  consent: (sessionId: string, decision: 'accept' | 'decline') =>
    api<{ session: CaptureSession }>('POST', '/atrium/api/capture/consent', { session_id: sessionId, decision }),
  stop: (roomId: string) => api<{ stopped: string; state: string }>('POST', '/atrium/api/capture/stop', { room_id: roomId }),
  setPolicy: (roomId: string, policy: CapturePolicy) =>
    api<{ policy: CapturePolicy }>('PUT', '/atrium/api/capture/policy', { room_id: roomId, policy }),
  sessions: (roomId: string, limit = 20) =>
    api<{ sessions: CaptureSession[] }>(
      'GET',
      `/atrium/api/capture/sessions?room_id=${encodeURIComponent(roomId)}&limit=${limit}`,
    ),
  transcript: (sessionId: string) =>
    api<CaptureTranscript>('GET', `/atrium/api/capture/transcript?session_id=${encodeURIComponent(sessionId)}`),
}
